<template>
  <FlotoFixedView>
    <FlotoScrollView>
      <FlotoCrudContainer
        ref="listRef"
        as-table
        :fetch-fn="fetchMyApprovals"
        :update-fn="updateApproverStatus"
        :columns="columns"
        v-bind="$attrs"
        default-sort="createdTime"
        sortable
        @selection-change="$emit('selection-change', $event)"
      >
        <template v-slot:add-controls="{ refreshList, items }">
          <FlotoPageHeader>
            <template v-slot:title>
              {{ $tc('my_approval', 2) }}
            </template>
            <template v-slot:title-append></template>
            <template v-slot:after-title>
              <ExportDrawer
                v-if="items.length > 0 && !isPortalLogin"
                :module-name="moduleName"
                :default-selected-columns="defaultSelectedColumns"
                :create-fn="handleExportSubmit"
                allow-pdf
              >
                <template v-slot:trigger="{ open }">
                  <MTooltip>
                    <template v-slot:trigger>
                      <MButton
                        variant="neutral-lighter"
                        class="text-neutral mx-1"
                        :shadow="false"
                        shape="circle"
                        @click="open"
                      >
                        <MIcon name="file-export" />
                      </MButton>
                    </template>
                    {{ $t('export') }}
                  </MTooltip>
                </template>
              </ExportDrawer>
              <MTooltip>
                <template v-slot:trigger>
                  <MButton
                    class="mx-1"
                    variant="neutral-lighter"
                    shape="circle"
                    :shadow="false"
                    @click="refreshList"
                  >
                    <MIcon name="sync" />
                  </MButton>
                </template>
                {{ $t('refresh') }}
              </MTooltip>
            </template>
          </FlotoPageHeader>
          <MRow class="mt-2 mb-2">
            <MCol>
              <FlotoSearchBar
                v-model="searchCriteria"
                :allow-keywords="false"
                :allow-save-search="false"
                :persist-criteria="false"
                :module-name="moduleName"
                @change="refreshList"
              />
            </MCol>
          </MRow>
          <MAffix @change="affixed = $event">
            <MTab
              v-model="currentStatus"
              :class="{ 'affixed-tab': affixed }"
              @change="refreshList"
            >
              <MTabPane v-for="tab in statusTabs" :key="tab" :tab="$t(tab)" />
            </MTab>
          </MAffix>
        </template>

        <template v-slot:form></template>
        <template v-slot:name="{ item }">
          <td class="resource-link">
            <FlotoLink
              :to="
                $currentModule.getRoute(viewRouteName || 'view', {
                  params: { id: item.approverId, moduleName: item.module },
                })
              "
            >
              {{ item.name }}
            </FlotoLink>
          </td>
        </template>
        <template v-slot:approvalRequesterName="{ item }">
          <td class="resource-link">
            <FlotoLink
              :to="
                $currentModule.getRoute(viewRouteName || 'view', {
                  params: { id: item.approverId, moduleName: item.module },
                  // @NOTE do not add query param in this url (for e-mail link this is not posible)
                })
              "
            >
              {{ item.approvalRequesterName }}
            </FlotoLink>
          </td>
        </template>
        <template v-slot:module="{ item }">
          <td class="text-ellipsis">
            {{ item.module ? $tc(item.module) : '---' }}
          </td>
        </template>
        <template v-slot:createdTime="{ item }">
          <td class="text-ellipsis">
            {{ item.createdAt | datetime }}
          </td>
        </template>
        <template v-slot:status="{ item }">
          <td class="text-ellipsis">
            <StatusIcon :status="item.status" show-status size="lg" />
            <div v-if="item.updatedAt" class="w-full">
              <small v-if="item.status !== 'pending'" class="text-neutral">
                {{ $t('by') }}
                <FlotoUserDrawer
                  v-if="item.approvedUserId"
                  :user-id="item.approvedUserId"
                  :disabled="isPortalLogin"
                />
                {{ $t('at') }}
                {{ item.updatedAt | datetime }}
              </small>
            </div>
          </td>
        </template>
        <template v-slot:actions="{ item }">
          <td>
            <div class="flex">
              <ApprovalAssetUpdateDrawer
                v-if="item.makerCheckerApproval"
                :stage-id="item.approvalId"
              />
              <FlotoLink
                as-button
                :to="
                  $currentModule.getRoute(viewRouteName || 'view', {
                    params: {
                      id: item.approverId,
                      moduleName: item.module,
                    },
                    // @NOTE do not add query param in this url (for e-mail link this is not posible)
                  })
                "
                shape="circle"
                variant="neutral-lighter"
                class="mr-2"
                :disabled="isDisabled(item)"
              >
                <MIcon name="eye" />
              </FlotoLink>
              <MTooltip v-if="item.status === 'pending'">
                <template v-slot:trigger>
                  <ApproveBtn
                    :item="item"
                    :approval-settings="approvalSettings"
                    @refresh="refresh"
                  >
                    <template v-slot:trigger="{ onUpdate }">
                      <MButton
                        shape="circle"
                        class="mr-2"
                        variant="success"
                        :disabled="isDisabled(item)"
                        @click="onUpdate"
                      >
                        <MIcon name="check" />
                      </MButton>
                    </template>
                    <template v-slot:header>
                      {{ item.name }}
                    </template>
                  </ApproveBtn>
                </template>
                {{ $t('approve') }}
              </MTooltip>
              <MTooltip v-if="item.status === 'pending'">
                <template v-slot:trigger>
                  <RejectBtn
                    :item="item"
                    :approval-settings="approvalSettings"
                    @refresh="refresh"
                  >
                    <template v-slot:trigger="{ onUpdate }">
                      <MButton
                        shape="circle"
                        class="mr-2"
                        variant="error"
                        :disabled="isDisabled(item)"
                        @click="onUpdate"
                      >
                        <MIcon name="times" />
                      </MButton>
                    </template>
                    <template v-slot:header>
                      {{ item.name }}
                    </template>
                  </RejectBtn>
                </template>
                {{ $t('reject') }}
              </MTooltip>
              <MTooltip v-if="item.status === 'pending'">
                <template v-slot:trigger>
                  <ReferBackBtn
                    :item="item"
                    :approval-settings="approvalSettings"
                    @refresh="refresh"
                  >
                    <template v-slot:trigger="{ onUpdate }">
                      <MButton
                        shape="circle"
                        class="mr-2"
                        variant="error"
                        :disabled="isDisabled(item)"
                        @click="onUpdate"
                      >
                        <MIcon name="undo" />
                      </MButton>
                    </template>
                    <template v-slot:header>
                      {{ item.name }}
                    </template>
                  </ReferBackBtn>
                </template>
                {{ $t('refer_back') }}
              </MTooltip>
            </div>
          </td>
        </template>
      </FlotoCrudContainer>
    </FlotoScrollView>
    <NoteRules ref="noteDrawer" :create-fn="handleNoteAdded" />
  </FlotoFixedView>
</template>

<script>
import CloneDeep from 'lodash/cloneDeep'
import {
  transformSearchCriteriasForServer,
  transformSearchCriteriaForServer,
} from '@data/search'
import { buildFlatQualificationStructure } from '@data/qualification'
import { authComputed } from '@state/modules/auth'
import StatusIcon from '@components/approval/components/approval-status-icon'
import { updateApproverApi } from '@components/approval/approval-api'
import { getApprovalSettingApi } from '@modules/approval/approval-api'
import NoteRules from '@components/note-rules/note-rules'
import { TechnicianComputed } from '@state/modules/technician'
import { getMyApprovalsApi } from '../my-approval-api'
import ApproveBtn from '../components/approve-btn.vue'
import RejectBtn from '../components/reject-btn.vue'
import ReferBackBtn from '../components/refer-back-btn.vue'
import { LicenseComputed } from '@state/modules/license'
import { exportDataApi } from '@modules/ticket/ticket-api'
import ExportDrawer from '@components/export/export-drawer'
import { getEncryptedPassword } from '@utils/password'
import { PreferenceComputed } from '@/src/state/modules/preference'
import ApprovalAssetUpdateDrawer from '@components/approval/components/approval-asset-update-drawer'

export default {
  name: 'MyApprovalList',
  components: {
    StatusIcon,
    NoteRules,
    ApproveBtn,
    ReferBackBtn,
    RejectBtn,
    ExportDrawer,
    ApprovalAssetUpdateDrawer,
  },
  props: {
    viewRouteName: { type: String, default: undefined },
    searchable: { type: Boolean, default: false },
    defaultQualifications: { type: Object, default: null },
    moduleName: {
      type: String,
      default() {
        return this.$constants.APPROVAL
      },
    },
  },
  data() {
    this.columns = [
      {
        name: this.$tc('name'),
        key: 'name',
      },
      { name: this.$tc('type'), key: 'module' },
      {
        name: `${this.$tc('requested')} ${this.$tc('by')}`,
        key: 'approvalRequesterName',
      },
      { name: this.$tc('subject'), key: 'subject', width: '20%' },
      { name: this.$tc('status'), key: 'status' },
      { name: this.$tc('created_time'), key: 'createdTime', sortable: true },
      {
        name: this.$tc('action', 2),
        key: 'actions',
        width: '250px',
        minWidth: '200px',
      },
    ]
    this.statusTabs = [
      'pending',
      'approved',
      'rejected',
      'ignored',
      'referred_back',
    ]
    return {
      currentStatus: 'pending',
      affixed: false,
      approvalSettings: {},
      pendingChanges: null,
      searchCriteria: transformSearchCriteriasForServer(this.moduleName, []),
    }
  },
  computed: {
    ...authComputed,
    ...TechnicianComputed,
    ...LicenseComputed,
    ...PreferenceComputed,
    defaultSelectedColumns() {
      return this.columns.map((c) => c.key)
    },
  },
  created() {
    getApprovalSettingApi().then((data) => (this.approvalSettings = data))
  },
  methods: {
    applyCriteria(searchCriteria, refreshList) {
      this.searchCriteria = searchCriteria
      refreshList()
    },
    refresh() {
      this.$refs.listRef.refresh()
    },
    handleNoteAdded(data) {
      return this.updateApproverStatus(
        { ...data, ...this.pendingChanges },
        true
      )
        .then((data) => {
          this.$refs.noteDrawer.closeForm()
          this.pendingChanges = null
          this.resolve(data)
        })
        .catch(() => this.reject())
    },
    fetchMyApprovals(limit, offset, sort) {
      const criterias = CloneDeep(this.searchCriteria)
      const q = transformSearchCriteriaForServer(this.moduleName, {
        operator: { value: 'equal' },
        paramName: 'approverStatus',
        value: this.currentStatus,
        valueType: 'enum',
      })
      criterias.quals.push(q)
      return getMyApprovalsApi(
        this.isPortalLogin,
        criterias,
        limit,
        offset,
        sort
      )
    },
    updateApproverStatus(data, forceUpdate = false) {
      if (
        data.status === 'rejected' &&
        this.approvalSettings.noteRequiredToReject &&
        forceUpdate === false
      ) {
        return new Promise((resolve, reject) => {
          this.pendingChanges = data
          this.$refs.noteDrawer.openForm()
          this.resolve = resolve
          this.reject = reject
        }).then(() => this.$refs.listRef.refresh())
      }
      return updateApproverApi(data).then(() => this.$refs.listRef.refresh())
    },
    isDisabled(item) {
      const currentModuleName =
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
          this.$constants.ASSET_MOVEMENT,
          this.$constants.ASSET_FIELD_TRANSITION_HISTORY,
        ].indexOf(item.module) >= 0
          ? this.$constants.ASSET
          : [this.$constants.CI_FIELD_TRANSITION_HISTORY].indexOf(
              item.module
            ) >= 0
          ? this.$constants.CMDB
          : item.module
      const availableModules = [
        ...this.availableModulesInLicense,
        this.$constants.KNOWLEDGE,
        this.$constants.USER,
      ]
      return availableModules.indexOf(currentModuleName) === -1
    },
    getAvailableAttachmentPassword(data) {
      const availableAttachmentPassword = data.protectedPassword
        ? getEncryptedPassword(data.protectedPassword)
        : getEncryptedPassword(this.attachmentPassword)
      return data.enabled ? availableAttachmentPassword : ''
    },
    handleExportSubmit(data) {
      const requestedData = {}
      const qualification = buildFlatQualificationStructure([
        ...((this.searchCriteria || {}).quals || []),
      ])
      qualification.quals.push(
        transformSearchCriteriaForServer(this.moduleName, {
          operator: { value: 'in' },
          paramName: 'approverStatus',
          value: [this.currentStatus],
          valueType: 'enum',
        })
      )
      requestedData.model = this.moduleName
      requestedData.exportFormat = data.exportFormat
      requestedData.enabled = data.enabled
      requestedData.attachmentPassword =
        this.getAvailableAttachmentPassword(data)
      const requestedColumns = []
      data.columns.forEach((c, i) =>
        requestedColumns.push({ propKey: c, orderNumber: i + 1 })
      )
      requestedData.columns = requestedColumns
      requestedData.qualification = this.defaultQualifications || qualification
      return exportDataApi(requestedData)
    },
  },
}
</script>
